import cookie from 'js-cookie'
import { InfaceAuthError } from '@inface-sdk/web-auth'
import { COOKIE } from '../../constants/GLOBAL_VARIABLE'
import Sentry from '../../config/sentry'
import { URI } from '../../utils/common/index'

export default class SSO {
  /**
   * @param {object} param
   */
  constructor({
    mainUrl, gameToken, infaceAuth, redirectUri, decodedRedirectUri,
  }) {
    this.mainUrl = mainUrl || 'https://signin.nexon.com'
    this.gameToken = gameToken
    this.infaceAuth = infaceAuth
    this.redirectUri = redirectUri
    this.decodedRedirectUri = decodedRedirectUri
  }

  /**
   * 스팀/콘솔 게스트 계정용 페이지에 해당하는 특정 도메인과 pathname 을 정의한 파일을 얻어오기 위한 함수입니다.
   * @returns {Array} [{ hostname, pathname }]
   */
  async _fetchSsoWhitelist() {
    try {
      const response = await fetch(`${this.mainUrl}/config/sso/whitelist-domains.json`)
      const whitelist = await response.json()

      Sentry.addBreadcrumb({
        type: 'debug',
        category: 'sso/whitelist-domains.json',
        data: {
          whitelist,
        },
        level: 'debug',
      })

      return whitelist
    } catch (e) {
      console.error('Failed to get SSO whitelist', e)

      Sentry.withScope((scope) => {
        scope.setLevel('error')
        Sentry.captureMessage('Failed to get SSO whitelist')
      })

      return []
    }
  }

  async _checkRedirect(key) {
    try {
      const whitelist = await this._fetchSsoWhitelist()
      return URI.isMatchUriInList(whitelist[key], this.decodedRedirectUri)
    } catch (e) {
      console.error('[_checkRedirect]', e)
      return false
    }
  }

  async issueWebTokenByGameToken({ gameToken, npToken }) {
    const ssoSession = cookie.get(COOKIE.WEB_TOKEN_SSO)
    if (ssoSession !== gameToken) {
      this.infaceAuth.clearSession() // 새로운 토큰으로 sso 호출 시마다 쿠키 제거
    }

    // { web_token: webToken, web_sso_session: webSSOSession }
    const webTokenInfo = await this.infaceAuth.getWebTokenByGameToken({ gameToken, npToken })

    Sentry.addBreadcrumb({
      type: 'debug',
      category: 'sso.webTokenInfo',
      message: '[getWebTokenByGameToken]',
      data: {
        gameToken,
        npToken,
        ...webTokenInfo,
      },
      level: 'debug',
    })

    if (!webTokenInfo.web_token) {
      throw webTokenInfo
    }
  }

  async issueWebTicketByGameToken({ gameToken, npToken }) {
    const domain = new URL(this.decodedRedirectUri).hostname
    // { web_ticket: webTicket }
    const webTicketInfo = await this.infaceAuth.getWebTicketByGameToken({ gameToken, npToken, domain })

    Sentry.addBreadcrumb({
      type: 'debug',
      category: 'sso.webTicketInfo',
      message: '[issueWebTicketByGameToken]',
      data: {
        gameToken,
        npToken,
        ...webTicketInfo,
      },
      level: 'debug',
    })

    if (!webTicketInfo.web_ticket) {
      throw webTicketInfo
    }

    return { ticket: webTicketInfo.web_ticket }
  }

  async loginCorsByGameToken({ gameToken, npToken, gid }) {
    const domain = new URL(this.decodedRedirectUri).origin
    // { web_ticket: webTicket }
    const webTicketInfo = await this.infaceAuth.getWebTicketByGameToken({
      gameToken, npToken, gid, domain, isCors: true,
    })

    Sentry.addBreadcrumb({
      type: 'debug',
      category: 'sso.webTicketInfo',
      message: '[loginCorsByGameToken]',
      data: {
        gameToken,
        npToken,
        gid,
        domain,
        ...webTicketInfo,
      },
      level: 'debug',
    })

    if (!webTicketInfo.web_ticket) {
      throw webTicketInfo
    }

    return { ticket: webTicketInfo.web_ticket }
  }

  async processTokenRoaming({ gid, gameToken }) {
    try {
      if (!gameToken) {
        throw new InfaceAuthError({ code: 113907, name: 'NOT_DEFINED_SSO_TOKEN', message: window.$t('NOT_DEFINED_SSO_TOKEN') })
      }

      const ssoData = {}
      const isGameToken = gameToken.startsWith('ias:gt')
      if (isGameToken) {
        ssoData.gameToken = gameToken
      } else {
        ssoData.npToken = gameToken
      }

      const isGuestRedirect = await this._checkRedirect('guest')
      const isCorsRedirect = await this._checkRedirect('cors')

      Sentry.addBreadcrumb({
        type: 'debug',
        category: '/sso',
        message: '[processTokenRoaming]',
        data: {
          gid,
          ...ssoData,
          redirectUri: this.decodedRedirectUri,
          isGuestRedirect,
          isCorsRedirect,
        },
        level: 'debug',
      })

      if (isGuestRedirect) {
        // 게스트 링킹 계정 sso (웹토큰 대신 웹티켓 발급)
        const { ticket } = await this.issueWebTicketByGameToken(ssoData)
        this.#navigateToRedirectUriWithoutHistory({ queryParam: { ticket } })
      } else if (isCorsRedirect) {
        // CORS 로그인용 웹티켓 발급
        const { ticket } = await this.loginCorsByGameToken({ ...ssoData, gid })
        // INSIGN CORS 로그인 연동과 동일하게 GAME_WEB/insign에서 WebToken을 구워서 최종 목적지(redirect_uri)로 이동해야 합니다.
        this.#navigateToRedirectUriWithoutHistory({
          queryParam: { ticket, redirect_uri: this.redirectUri },
          redirectUri: `${this.redirectUri}/insign`, // GAME_WEB/insign에서 Web SDK 연동 필요
        })
      } else {
        // 기존 sso
        await this.issueWebTokenByGameToken(ssoData)
        this.#navigateToRedirectUriWithoutHistory()
      }
    } catch (e) {
      console.error('[_processTokenRoaming]', e, e instanceof InfaceAuthError ? e.data : '')
      const err = e instanceof InfaceAuthError ? e : new InfaceAuthError({
        code: 113902, name: 'FAIL_TO_TOKEN_ROAMING', message: window.$t('FAIL_TO_TOKEN_ROAMING'), data: e,
      })
      throw err
    }
  }

  /**
   * 웹티켓으로 웹토큰 발급
   * /private/v2/issue/web-ticket/by-game-token (w/ guid, nptoken, gid)
   * @param {string} webTicket // game token으로 만든 웹티켓
   */
  async issueWebTokenByWebTicket({ webTicket, gid, domain }) {
    if (!webTicket) {
      throw new InfaceAuthError({ code: 113908, name: 'NOT_DEFINED_SSO_TICKET', message: window.$t('NOT_DEFINED_SSO_TICKET') })
    }
    if (!gid) {
      throw new InfaceAuthError({ code: 113916, name: 'NOT_DEFINED_GID', message: window.$t('NOT_DEFINED_GID') })
    }

    // { web_token: webToken }
    const webTokenInfo = await this.infaceAuth.getWebTokenByWebTicketFromGameToken({ webTicket, gid, domain })

    Sentry.addBreadcrumb({
      type: 'debug',
      category: 'sso.webTokenInfo',
      message: '[issueWebTokenByWebTicket]',
      data: {
        gid,
        domain,
        webTicket,
        ...webTokenInfo,
      },
      level: 'debug',
    })

    if (!webTokenInfo.web_token) {
      throw webTokenInfo
    }

    return { webToken: webTokenInfo.web_token }
  }

  async processTokenRoamingByTicket({ gid, webTicket }) {
    try {
      const isGuestRedirect = await this._checkRedirect('guest')
      const domain = new URL(this.decodedRedirectUri).hostname

      Sentry.addBreadcrumb({
        type: 'debug',
        category: '/sso-by-ticket',
        message: '[processTokenRoamingByTicket]',
        data: {
          gid,
          webTicket,
          redirectUri: this.decodedRedirectUri,
          isGuestRedirect,
        },
        level: 'debug',
      })

      if (isGuestRedirect) {
        // 게스트 링킹 계정 sso (웹티켓 전달)
        this.#navigateToRedirectUriWithoutHistory({ queryParam: { ticket: webTicket } })
      } else {
        // 넥슨 계정 sso (웹토큰 발급)
        await this.issueWebTokenByWebTicket({
          gid,
          webTicket,
          domain,
        })

        this.#navigateToRedirectUriWithoutHistory()
      }
    } catch (e) {
      const err = e instanceof InfaceAuthError ? e : new InfaceAuthError({
        code: 113917, name: 'FAIL_TO_TOKEN_ROAMING_BY_WEBTICKET', message: window.$t('FAIL_TO_TOKEN_ROAMING_BY_WEBTICKET'), data: e,
      })

      throw err
    }
  }

  #navigateToRedirectUriWithoutHistory({ queryParam = {}, redirectUri = this.redirectUri } = {}) {
    const url = new URL(redirectUri)
    Object.keys(queryParam).forEach((key) => {
      url.searchParams.append(key, queryParam[key])
    })
    console.debug('[navigateToRedirectUriWithoutHistory] url', url)
    window.location.replace(url)
  }
}
